import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowRightLeft, BookmarkPlus, Check, FileDown, Loader2, Plus, Repeat, Send, Sparkles, Trash2, Undo2, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { FormField } from '@/components/ui/form-field';
import { NativeSelect } from '@/components/ui/native-select';
import { ernaehrungAPI } from '@/lib/api';
import { MAHLZEIT, AUSSCHLUSS_NAME, datum, fehlertext } from '@/pages/ErnaehrungPage';
import { TauschDialog } from '@/pages/ErnaehrungMitgliedPage';
import { Preis } from '@/components/ErnaehrungPreis';
import { VariantenEditor, VariantenNeu } from '@/components/PlanVarianten';

/* E4 Plan-Builder (15.10.2026, Soll-Design docs/ernaehrung/mockups/PlanBuilder.dc.html): links das Setup — kcal mit
   „Empfehlung übernehmen“ (Tagesziel aus E1/Bedarf, je Tag), Makro-Verteilung mit Presets und eigenen Splits, Mahlzeiten
   an/aus mit Anteilen (Summe 100 %), Ausschlüsse aus dem Kundenprofil, „Plan generieren“ — rechts der Tag mit
   Live-Makrobalken je Mahlzeit und Abweichung in g (± 5 %). Gerechnet wird NUR auf dem Server (`stand` je Mahlzeit und
   Tag); hier wird angezeigt und je Änderung die `version` mitgeschickt. Pläne sind entwurf | zugewiesen — die App sieht
   nur zugewiesene. */

const SLOTS = ['fruehstueck', 'mittag', 'abend', 'snack'];
const ANTEIL_STANDARD = { fruehstueck: 25, mittag: 35, abend: 30, snack: 10 };
const MAKROS = [['eiweiss', 'Protein', 'P'], ['kh', 'Kohlenhydrate', 'K'], ['fett', 'Fett', 'F']];
const komma = (v, stellen = 1) => Number(v || 0).toLocaleString('de-DE', { maximumFractionDigits: stellen });
const kcalText = (v) => Number(v || 0).toLocaleString('de-DE');
const vorzeichen = (v) => (v > 0 ? `+${komma(v)}` : v < 0 ? `−${komma(Math.abs(v))}` : '±0');
const heuteIso = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
const STATUS = { entwurf: { label: 'Entwurf', cls: 'bg-muted text-muted-foreground' }, zugewiesen: { label: 'Zugewiesen', cls: 'bg-emerald-500/10 text-emerald-700 border-emerald-200' },
  aktiv: { label: 'Zugewiesen', cls: 'bg-emerald-500/10 text-emerald-700 border-emerald-200' }, abgeloest: { label: 'Abgelöst', cls: 'bg-amber-500/10 text-amber-700 border-amber-200' } };

function gramm(kcal, pct, kcalJeG) { return kcal ? Math.round((kcal * pct) / 100 / kcalJeG) : null; }

/* Ein Balken je Makro: Ist gegen Ziel, der helle Bereich markiert ± 5 % — Werte kommen fertig vom Server (`stand`). */
function MakroBalken({ stand, klein }) {
  if (!stand) return null;
  return (
    <div className={`grid gap-x-3 gap-y-1 ${klein ? 'grid-cols-3' : 'grid-cols-1 sm:grid-cols-3'}`}>
      {MAKROS.map(([k, label, kurz]) => {
        const ziel = stand.ziel[k] || 0;
        const ist = ziel + stand.abweichung[k];
        const anteil = ziel ? Math.min(1.5, ist / ziel) : 0;
        const weg = ziel ? Math.abs(stand.abweichung[k]) / ziel > 0.05 : false;
        return (
          <div key={k} className="min-w-0">
            <div className="flex items-baseline justify-between gap-1 text-xs">
              <span className="truncate">{klein ? kurz : label}</span>
              <span className={`tabular-nums whitespace-nowrap ${weg ? 'text-amber-700' : 'text-muted-foreground'}`}>{komma(ist, 0)} / {komma(ziel, 0)} g</span>
            </div>
            <div className="relative h-1.5 mt-0.5 rounded-full bg-muted overflow-hidden" aria-hidden="true">
              <div className="absolute inset-y-0 bg-primary/15" style={{ left: `${(0.95 / 1.5) * 100}%`, width: `${(0.1 / 1.5) * 100}%` }} />
              <div className={`absolute inset-y-0 left-0 rounded-full ${weg ? 'bg-amber-500' : 'bg-primary'}`} style={{ width: `${(anteil / 1.5) * 100}%` }} />
              <div className="absolute inset-y-0 w-px bg-foreground/40" style={{ left: `${(1 / 1.5) * 100}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Abweichung({ stand }) {
  if (!stand) return null;
  const z = stand.ziel;
  return (
    <p className={`text-xs tabular-nums ${stand.treffer ? 'text-muted-foreground' : 'text-amber-700'}`}>
      Ziel {kcalText(z.kcal)} kcal · {kcalText(z.kcal - stand.toleranz_kcal)}–{kcalText(z.kcal + stand.toleranz_kcal)}
      {' · '}{MAKROS.map(([k, , kurz]) => `${kurz} ${vorzeichen(stand.abweichung[k])} g`).join(' · ')}
      {stand.treffer ? '' : ` · kcal ${vorzeichen(stand.abweichung.kcal)}`}
    </p>
  );
}

/* Zutat mit Menge und Einheit (g/ml oder Haushaltsmaß des Lebensmittels) — Enter oder Verlassen speichert über den Server. */
function ZutatZeile({ z, lm, onAendern, gesperrtEdit }) {
  const einheiten = ['g', ...((lm?.einheiten || []).map((e) => e.name))];
  const [menge, setMenge] = useState(z.einheit ? String(z.menge) : String(z.gramm));
  const [einheit, setEinheit] = useState(z.einheit || 'g');
  useEffect(() => { setMenge(z.einheit ? String(z.menge) : String(z.gramm)); setEinheit(z.einheit || 'g'); }, [z.gramm, z.menge, z.einheit]);
  const speichern = () => {
    const wert = Number(String(menge).replace(',', '.'));
    if (!wert || wert <= 0) { setMenge(z.einheit ? String(z.menge) : String(z.gramm)); return; }
    if (einheit === (z.einheit || 'g') && wert === (z.einheit ? z.menge : z.gramm)) return;
    onAendern(z.lebensmittel_id, wert, einheit === 'g' ? (lm?.basis || 'g') : einheit);
  };
  return (
    <li className="flex items-center gap-2 text-sm">
      <span className="min-w-0 flex-1 break-words">{z.name}{z.gesperrt && <span className="ml-1 text-[10px] uppercase tracking-wide text-muted-foreground" title="Im Rezept gesperrt — der Generator skaliert sie nicht">fix</span>}</span>
      <span className="text-xs text-muted-foreground tabular-nums whitespace-nowrap">{z.naehrwerte?.kcal ?? '–'} kcal</span>
      <Input aria-label={`Menge ${z.name}`} inputMode="decimal" value={menge} disabled={gesperrtEdit} onChange={(e) => setMenge(e.target.value)} onBlur={speichern}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); } }} className="h-7 w-16 text-right text-sm tabular-nums px-1.5" />
      <NativeSelect aria-label={`Einheit ${z.name}`} value={einheit} disabled={gesperrtEdit} onChange={(v) => { setEinheit(v); if (v !== einheit) { const wert = Number(String(menge).replace(',', '.')); if (wert > 0) onAendern(z.lebensmittel_id, wert, v === 'g' ? (lm?.basis || 'g') : v); } }}
        options={einheiten.map((e) => ({ value: e, label: e === 'g' ? (lm?.basis || 'g') : e }))} className="h-7 w-24 py-0 px-1.5 text-sm" />
      {z.einheit && <span className="text-xs text-muted-foreground tabular-nums whitespace-nowrap">= {z.gramm} g</span>}
    </li>
  );
}

function VerschiebenWahl({ plan, tag, m, onWahl, onClose }) {
  const [zuDatum, setZuDatum] = useState(tag.datum);
  const [zuSlot, setZuSlot] = useState(SLOTS.find((s) => s !== m.slot));
  const zielTag = plan.tage.find((t) => t.datum === zuDatum);
  const belegt = zielTag?.mahlzeiten.find((x) => x.slot === zuSlot);
  return (
    <div className="rounded-md border p-2 space-y-2 text-sm" role="group" aria-label="Mahlzeit verschieben">
      <div className="grid grid-cols-2 gap-2">
        <FormField label="Tag"><NativeSelect value={zuDatum} onChange={setZuDatum} options={plan.tage.map((t) => ({ value: t.datum, label: datum(t.datum, { weekday: 'short', day: '2-digit', month: '2-digit' }) }))} className="h-8 py-0" /></FormField>
        <FormField label="Mahlzeit"><NativeSelect value={zuSlot} onChange={setZuSlot} options={SLOTS.filter((s) => !(zuDatum === tag.datum && s === m.slot)).map((s) => ({ value: s, label: MAHLZEIT[s] }))} className="h-8 py-0" /></FormField>
      </div>
      <p className="text-xs text-muted-foreground">{belegt ? `Tauscht den Platz mit „${belegt.name}“.` : 'Die Stelle ist frei — die Mahlzeit zieht um, die alte Stelle bleibt leer.'}</p>
      <div className="flex gap-2">
        <Button size="sm" onClick={() => onWahl(zuDatum, zuSlot)}><ArrowRightLeft className="w-4 h-4 mr-1" />{belegt ? 'Tauschen' : 'Verschieben'}</Button>
        <Button size="sm" variant="ghost" onClick={onClose}>Abbrechen</Button>
      </div>
    </div>
  );
}

function EditorMahlzeit({ plan, tag, m, lebensmittel, darf, onZutat, onVerschieben, onTauschen }) {
  const [verschieben, setVerschieben] = useState(false);
  const lmJe = (id) => lebensmittel[id];
  const genuss = !!m.genuss_id;
  return (
    <div className={`rounded-lg border p-3 space-y-2 ${m.stand && !m.stand.treffer ? 'border-amber-300' : ''}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">{m.uhrzeit ? `${m.uhrzeit} · ` : ''}{MAHLZEIT[m.slot]}</p>
          <h4 className="font-semibold leading-snug break-words">{m.name}</h4>
          <p className="text-xs text-muted-foreground tabular-nums">{m.naehrwerte.kcal} kcal · P {komma(m.naehrwerte.eiweiss, 0)} · K {komma(m.naehrwerte.kh, 0)} · F {komma(m.naehrwerte.fett, 0)} g{m.minuten ? ` · ${m.minuten} Min` : ''}{m.preis ? <> · <Preis p={m.preis} personen={m.personen || 1} /></> : null}</p>
        </div>
        <div className="flex flex-wrap gap-1">
          {m.optimiert && <Badge variant="outline" className="font-normal" title="Mengen je Zutat auf Kalorien und Makros der Mahlzeit abgestimmt">optimiert</Badge>}
          {m.bearbeitet && <Badge variant="outline" className="font-normal">bearbeitet</Badge>}
          {genuss && <Badge variant="secondary" className="font-normal">Genuss</Badge>}
          {m.stand?.treffer && <Badge variant="outline" className="font-normal bg-emerald-500/10 text-emerald-700 border-emerald-200"><Check className="w-3 h-3 mr-0.5" aria-hidden="true" />± 5 %</Badge>}
        </div>
      </div>
      <Abweichung stand={m.stand} />
      <MakroBalken stand={m.stand} klein />
      {!genuss && (
        <details className="group rounded-md border px-2.5 py-1.5" open={darf}>
          <summary className="cursor-pointer text-sm font-medium list-none">Zutaten ({m.zutaten.length}){m.personen > 1 ? ` · für ${m.personen} Personen` : ''}</summary>
          <ul className="mt-1.5 space-y-1">
            {m.zutaten.map((z) => <ZutatZeile key={z.lebensmittel_id} z={z} lm={lmJe(z.lebensmittel_id)} gesperrtEdit={!darf} onAendern={(lid, menge, einheit) => onZutat(tag.datum, m.slot, lid, menge, einheit)} />)}
            {m.gewuerze?.length > 0 && <li className="text-xs text-muted-foreground">Nach Belieben: {m.gewuerze.join(', ')}</li>}
          </ul>
        </details>
      )}
      {darf && !genuss && (
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={onTauschen}><Repeat className="w-4 h-4 mr-1" />Rezept tauschen</Button>
          <Button size="sm" variant="outline" aria-expanded={verschieben} onClick={() => setVerschieben(!verschieben)}><ArrowRightLeft className="w-4 h-4 mr-1" />Verschieben</Button>
        </div>
      )}
      {verschieben && <VerschiebenWahl plan={plan} tag={tag} m={m} onClose={() => setVerschieben(false)} onWahl={(d, s) => { setVerschieben(false); onVerschieben(tag.datum, m.slot, d, s); }} />}
    </div>
  );
}

/* Setup (linke Spalte des Mockups). `empfehlung` kommt vom Server: Tagesziel je Tag aus Ziele & Zugang (E1) bzw. Bedarf. */
function Setup({ leadId, profil, liste, vorlagen, onErzeugt, onSplitsGeaendert }) {
  const [f, setF] = useState({ start: heuteIso(), tage: '7', name: '', vorlage_id: '', kcal_fest: false, kcal: '', split_wahl: 'empfehlung',
    eigene: { eiweiss_pct: '30', kh_pct: '40', fett_pct: '30' }, mahlzeiten: Object.fromEntries(SLOTS.map((s) => [s, { an: true, anteil: ANTEIL_STANDARD[s] }])), zuweisen: false });
  const [empfehlung, setEmpfehlung] = useState(null);
  const [laeuft, setLaeuft] = useState('');
  const [splitName, setSplitName] = useState('');
  useEffect(() => {
    let aktuell = true;
    ernaehrungAPI.planEmpfehlung(leadId, { start: f.start, tage: Number(f.tage) }).then((r) => { if (aktuell) setEmpfehlung(r.data); }).catch(() => { if (aktuell) setEmpfehlung(null); });
    return () => { aktuell = false; };
  }, [leadId, f.start, f.tage, profil?.ziele?.kcal]);
  const setze = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const presets = useMemo(() => liste?.presets || [], [liste]);
  const splits = useMemo(() => liste?.splits || [], [liste]);
  const split = useMemo(() => {
    if (f.split_wahl === 'empfehlung') return empfehlung?.split || null;
    if (f.split_wahl === 'eigen') return { eiweiss_pct: Number(f.eigene.eiweiss_pct) || 0, kh_pct: Number(f.eigene.kh_pct) || 0, fett_pct: Number(f.eigene.fett_pct) || 0 };
    return presets.find((p) => p.id === f.split_wahl) || splits.find((s) => s.id === f.split_wahl) || null;
  }, [f.split_wahl, f.eigene, empfehlung, presets, splits]);
  const splitSumme = split ? split.eiweiss_pct + split.kh_pct + split.fett_pct : 0;
  const kcalVorschau = f.kcal_fest ? Number(f.kcal) || null : empfehlung?.kcal || null;
  const an = SLOTS.filter((s) => f.mahlzeiten[s].an);
  const summe = an.reduce((s, k) => s + (Number(f.mahlzeiten[k].anteil) || 0), 0);
  const setzeMahlzeit = (s, patch) => setF((x) => ({ ...x, mahlzeiten: { ...x.mahlzeiten, [s]: { ...x.mahlzeiten[s], ...patch } } }));
  const gleichVerteilen = () => {
    const rest = { ...ANTEIL_STANDARD };
    const summeAn = an.reduce((s, k) => s + rest[k], 0);
    const neu = {};
    let vergeben = 0;
    an.forEach((k, i) => { const a = i === an.length - 1 ? 100 - vergeben : Math.round((rest[k] / summeAn) * 100); vergeben += a; neu[k] = a; });
    setF((x) => ({ ...x, mahlzeiten: Object.fromEntries(SLOTS.map((s) => [s, { ...x.mahlzeiten[s], anteil: neu[s] ?? x.mahlzeiten[s].anteil }])) }));
  };
  const setup = () => ({
    kcal_modus: f.kcal_fest ? 'manuell' : 'empfehlung', kcal: f.kcal_fest ? Number(f.kcal) : null,
    split_id: ['empfehlung', 'eigen'].includes(f.split_wahl) ? null : f.split_wahl,
    split: f.split_wahl === 'eigen' ? split : null,
    mahlzeiten: an.map((s) => ({ slot: s, anteil: Number(f.mahlzeiten[s].anteil) || 0 })),
  });
  const bereit = summe === 100 && an.length >= 2 && (!f.kcal_fest || (Number(f.kcal) >= 1200 && Number(f.kcal) <= 8000)) && (f.split_wahl !== 'eigen' || splitSumme === 100)
    && (f.kcal_fest || empfehlung?.kcal);
  const erzeugen = async () => {
    setLaeuft('erzeugen');
    try {
      const { data } = await ernaehrungAPI.planMitSetup(leadId, { start: f.start, tage: Number(f.tage), vorlage_id: f.vorlage_id || null, setup: setup(),
        status: f.zuweisen ? 'zugewiesen' : 'entwurf', name: f.name.trim() || null });
      toast.success(f.zuweisen ? 'Plan erzeugt und zugewiesen — das Mitglied sieht ihn in der App' : 'Plan als Entwurf erzeugt — prüfen, dann zuweisen');
      onErzeugt(data);
    } catch (e) { toast.error(fehlertext(e)); } finally { setLaeuft(''); }
  };
  const splitSpeichern = async () => {
    if (splitName.trim().length < 2) { toast.error('Name für die Verteilung (2 bis 40 Zeichen)'); return; }
    setLaeuft('split');
    try {
      const { data } = await ernaehrungAPI.splitSpeichern({ name: splitName.trim(), ...split });
      const neu = data.splits[data.splits.length - 1];
      toast.success(`„${neu.name}“ gespeichert`); setSplitName(''); onSplitsGeaendert(data.splits); setF((x) => ({ ...x, split_wahl: neu.id }));
    } catch (e) { toast.error(fehlertext(e)); } finally { setLaeuft(''); }
  };
  const splitLoeschen = async (s) => {
    try { const { data } = await ernaehrungAPI.splitLoeschen(s.id); onSplitsGeaendert(data.splits); if (f.split_wahl === s.id) setze('split_wahl')('empfehlung'); }
    catch (e) { toast.error(fehlertext(e)); }
  };
  const ohneNamen = (profil?.ohne_lebensmittel_namen || []);
  const knopf = (aktiv, label, onClick, titel) => (
    <button type="button" key={label} aria-pressed={aktiv} title={titel} onClick={onClick}
      className={`rounded-md border px-2.5 py-1 text-sm text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${aktiv ? 'bg-primary text-primary-foreground border-primary' : 'hover:bg-accent'}`}>{label}</button>
  );
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-2">
        <FormField label="Start"><Input type="date" value={f.start} onChange={(e) => setze('start')(e.target.value)} /></FormField>
        <FormField label="Dauer"><NativeSelect value={f.tage} onChange={setze('tage')} options={[3, 5, 7, 10, 14].map((n) => ({ value: String(n), label: `${n} Tage` }))} /></FormField>
        <FormField label="Name (optional)" className="col-span-2"><Input maxLength={80} placeholder="z. B. Fettabbau Phase 1" value={f.name} onChange={(e) => setze('name')(e.target.value)} /></FormField>
        <FormField label="Grundlage" className="col-span-2"><NativeSelect value={f.vorlage_id} onChange={setze('vorlage_id')} options={[{ value: '', label: 'automatisch aus den Rezepten' }, ...vorlagen.map((v) => ({ value: v.id, label: `Vorlage: ${v.name}${v.setup?.split ? ' (mit Setup)' : ''}` }))]} /></FormField>
      </div>

      <section className="space-y-1.5">
        <h4 className="text-sm font-semibold">Kalorien pro Tag</h4>
        <div className="flex flex-wrap gap-1.5">
          {knopf(!f.kcal_fest, empfehlung?.kcal ? `Empfehlung · je Tag aus den Zielen` : 'Empfehlung', () => setze('kcal_fest')(false), 'Tagesziel je Tag aus Ziele & Zugang (Trainingstag, Ruhetag …) bzw. aus dem Bedarf')}
          {knopf(f.kcal_fest, 'Fest', () => setze('kcal_fest')(true))}
        </div>
        {f.kcal_fest ? (
          <div className="flex items-end gap-2">
            <FormField label="kcal"><Input type="number" min="1200" max="8000" value={f.kcal} onChange={(e) => setze('kcal')(e.target.value)} className="w-28" /></FormField>
            {empfehlung?.kcal && <Button type="button" size="sm" variant="outline" onClick={() => setze('kcal')(String(empfehlung.kcal))}>Empfehlung {kcalText(empfehlung.kcal)} übernehmen</Button>}
          </div>
        ) : empfehlung?.tage?.length ? (
          <ul className="flex flex-wrap gap-1 text-xs">
            {empfehlung.tage.map((t) => <li key={t.datum} className={`rounded border px-1.5 py-0.5 tabular-nums ${t.pausiert ? 'text-muted-foreground' : ''}`} title={t.modus}>{datum(t.datum, { weekday: 'short' })} {kcalText(t.kcal)}{t.modus !== 'standard' ? ` · ${t.modus}` : ''}</li>)}
          </ul>
        ) : <p className="text-xs text-amber-700" role="status">{empfehlung?.fehlt || 'Noch kein Tagesziel — Kundenprofil vervollständigen oder kcal fest vorgeben.'}</p>}
      </section>

      <section className="space-y-1.5">
        <h4 className="text-sm font-semibold">Makro-Verteilung</h4>
        <div className="flex flex-wrap gap-1.5">
          {knopf(f.split_wahl === 'empfehlung', 'Wie Tagesziel', () => setze('split_wahl')('empfehlung'), 'Makros des Tagesziels (Bedarf bzw. Tagesprofil)')}
          {presets.map((p) => knopf(f.split_wahl === p.id, `${p.name} ${p.eiweiss_pct}/${p.kh_pct}/${p.fett_pct}`, () => setze('split_wahl')(p.id)))}
          {splits.map((s) => (
            <span key={s.id} className="inline-flex items-stretch">
              {knopf(f.split_wahl === s.id, `${s.name} ${s.eiweiss_pct}/${s.kh_pct}/${s.fett_pct}`, () => setze('split_wahl')(s.id))}
              <button type="button" aria-label={`${s.name} löschen`} onClick={() => splitLoeschen(s)} className="ml-0.5 rounded-md border px-1 text-muted-foreground hover:text-destructive"><X className="w-3 h-3" /></button>
            </span>
          ))}
          {knopf(f.split_wahl === 'eigen', '+ Eigene', () => setze('split_wahl')('eigen'))}
        </div>
        {f.split_wahl === 'eigen' && (
          <div className="space-y-1.5">
            <div className="grid grid-cols-3 gap-1.5">
              {[['eiweiss_pct', 'P %'], ['kh_pct', 'K %'], ['fett_pct', 'F %']].map(([k, l]) => (
                <FormField key={k} label={l}><Input type="number" min="5" max="70" value={f.eigene[k]} onChange={(e) => setF((x) => ({ ...x, eigene: { ...x.eigene, [k]: e.target.value } }))} /></FormField>
              ))}
            </div>
            <p className={`text-xs ${splitSumme === 100 ? 'text-muted-foreground' : 'text-amber-700'}`}>Summe {splitSumme} %{splitSumme === 100 ? '' : ' — muss 100 % ergeben'}</p>
            <div className="flex gap-1.5">
              <Input aria-label="Name der Verteilung" placeholder="als … speichern" maxLength={40} value={splitName} onChange={(e) => setSplitName(e.target.value)} className="h-8" />
              <Button type="button" size="sm" variant="outline" disabled={laeuft === 'split' || splitSumme !== 100} onClick={splitSpeichern}><Plus className="w-4 h-4 mr-1" />Speichern</Button>
            </div>
          </div>
        )}
        {split && kcalVorschau && (
          <p className="text-xs text-muted-foreground tabular-nums">P {gramm(kcalVorschau, split.eiweiss_pct, 4)} g · K {gramm(kcalVorschau, split.kh_pct, 4)} g · F {gramm(kcalVorschau, split.fett_pct, 9)} g bei {kcalText(kcalVorschau)} kcal</p>
        )}
      </section>

      <section className="space-y-1.5">
        <div className="flex items-center justify-between"><h4 className="text-sm font-semibold">Mahlzeiten &amp; Verteilung</h4><Button type="button" size="sm" variant="ghost" onClick={gleichVerteilen}>Standard</Button></div>
        {SLOTS.map((s) => {
          const m = f.mahlzeiten[s];
          return (
            <div key={s} className="grid grid-cols-[7rem_1fr_3.5rem] items-center gap-2">
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={m.an} disabled={m.an && an.length <= 2} onChange={(e) => setzeMahlzeit(s, { an: e.target.checked })} />{MAHLZEIT[s]}</label>
              <input type="range" min="5" max="70" step="5" aria-label={`Anteil ${MAHLZEIT[s]}`} disabled={!m.an} value={m.anteil} onChange={(e) => setzeMahlzeit(s, { anteil: Number(e.target.value) })} className="w-full accent-primary" />
              <Input inputMode="numeric" aria-label={`Anteil ${MAHLZEIT[s]} in Prozent`} disabled={!m.an} value={m.anteil} onChange={(e) => setzeMahlzeit(s, { anteil: e.target.value })} className="h-7 text-right text-sm px-1.5" />
            </div>
          );
        })}
        <p className={`text-xs ${summe === 100 ? 'text-muted-foreground' : 'text-amber-700'}`} aria-live="polite">Summe {summe} %{summe === 100 ? '' : ' — muss 100 % ergeben'}{an.length < 2 ? ' · mindestens zwei Mahlzeiten' : ''}</p>
      </section>

      <section className="space-y-1">
        <h4 className="text-sm font-semibold">Ausschließen <span className="font-normal text-muted-foreground">(aus dem Kundenprofil)</span></h4>
        <div className="flex flex-wrap gap-1">
          {(profil?.ausschluesse || []).map((a) => <Badge key={a} variant="secondary" className="font-normal">{AUSSCHLUSS_NAME[a] || a}</Badge>)}
          {ohneNamen.map((n) => <Badge key={n} variant="secondary" className="font-normal">mag nicht: {n}</Badge>)}
          {!(profil?.ausschluesse || []).length && !ohneNamen.length && <span className="text-xs text-muted-foreground">nichts ausgeschlossen</span>}
        </div>
      </section>

      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.zuweisen} onChange={(e) => setze('zuweisen')(e.target.checked)} />Sofort zuweisen (sonst Entwurf)</label>
      <Button type="button" className="w-full" disabled={!bereit || laeuft === 'erzeugen'} onClick={erzeugen}>
        {laeuft === 'erzeugen' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}Plan generieren
      </Button>
    </div>
  );
}

export default function PlanEditor({ leadId, profil, heute, onPlanGeaendert }) {
  const navigate = useNavigate();
  const [liste, setListe] = useState(null);
  const [vorlagen, setVorlagen] = useState([]);
  const [lebensmittel, setLebensmittel] = useState({});
  const [gewaehlt, setGewaehlt] = useState(null);         // Plan-ID
  const [plan, setPlan] = useState(null);
  const [aktiv, setAktiv] = useState(null);               // Datum
  const [tausch, setTausch] = useState(null);
  const [setupOffen, setSetupOffen] = useState(true);
  const [variantenNeu, setVariantenNeu] = useState(false);   // W3.2: links „Plan mit Varianten“ statt Setup
  const [laeuft, setLaeuft] = useState('');
  const ladenListe = useCallback(async () => {
    try { setListe((await ernaehrungAPI.plaene(leadId)).data); } catch (e) { toast.error(fehlertext(e, 'Pläne konnten nicht geladen werden')); }
  }, [leadId]);
  const ladenPlan = useCallback(async (id) => {
    if (!id) { setPlan(null); return; }
    try { setPlan((await ernaehrungAPI.planLesen(leadId, id)).data); } catch (e) { toast.error(fehlertext(e, 'Plan konnte nicht geladen werden')); setPlan(null); }
  }, [leadId]);
  useEffect(() => { ladenListe(); }, [ladenListe]);
  useEffect(() => { ladenPlan(gewaehlt); }, [gewaehlt, ladenPlan]);
  useEffect(() => {
    ernaehrungAPI.vorlagen().then((r) => setVorlagen(r.data.vorlagen)).catch(() => {});
    ernaehrungAPI.rezepte().then((r) => setLebensmittel(Object.fromEntries(r.data.lebensmittel.map((l) => [l.id, l])))).catch(() => {});
  }, []);
  useEffect(() => {
    if (!plan) return;
    setAktiv((a) => (plan.tage.some((t) => t.datum === a) ? a : (plan.tage.find((t) => t.datum === heute) || plan.tage[0])?.datum));
  }, [plan, heute]);
  const tag = plan?.tage.find((t) => t.datum === aktiv);
  const profilMitNamen = useMemo(() => ({ ...profil, ohne_lebensmittel_namen: (profil?.ohne_lebensmittel || []).map((id) => lebensmittel[id]?.name || id) }), [profil, lebensmittel]);

  const nachAenderung = (data, zweiterTag) => {
    setPlan((p) => {
      if (!p) return p;
      const tage = p.tage.map((t) => (t.datum === data.tag.datum ? data.tag : zweiterTag && t.datum === zweiterTag.datum ? zweiterTag : t));
      return { ...p, tage, version: data.version };
    });
  };
  const zutat = async (datumTag, slot, lid, menge, einheit) => {
    try { const { data } = await ernaehrungAPI.zutatAendern(leadId, plan.id, datumTag, { mahlzeit: slot, lebensmittel_id: lid, menge, einheit, version: plan.version }); nachAenderung(data); }
    catch (e) { toast.error(fehlertext(e)); if (e?.response?.status === 409) ladenPlan(plan.id); }
  };
  const verschieben = async (datumTag, slot, zuDatum, zuSlot) => {
    try {
      const { data } = await ernaehrungAPI.mahlzeitVerschieben(leadId, plan.id, datumTag, { mahlzeit: slot, zu_datum: zuDatum, zu_mahlzeit: zuSlot, version: plan.version });
      nachAenderung(data, data.von_tag); setAktiv(zuDatum); toast.success(data.von_tag ? 'Verschoben' : 'Getauscht');
    } catch (e) { toast.error(fehlertext(e)); if (e?.response?.status === 409) ladenPlan(plan.id); }
  };
  const status = async (neu) => {
    setLaeuft('status');
    try {
      const { data } = await ernaehrungAPI.planStatus(leadId, plan.id, neu);
      setPlan(data); ladenListe(); onPlanGeaendert?.();
      toast.success(neu === 'zugewiesen' ? 'Zugewiesen — das Mitglied sieht den Plan in der App' : 'Zuweisung aufgehoben — der Plan bleibt als Entwurf');
    } catch (e) { toast.error(fehlertext(e)); } finally { setLaeuft(''); }
  };
  const loeschen = async () => {
    if (!window.confirm('Plan endgültig löschen? „Zuweisung aufheben“ behält ihn als Entwurf.')) return;
    setLaeuft('loeschen');
    try { await ernaehrungAPI.planLoeschen(leadId, plan.id); toast.success('Plan gelöscht'); setGewaehlt(null); ladenListe(); onPlanGeaendert?.(); }
    catch (e) { toast.error(fehlertext(e)); } finally { setLaeuft(''); }
  };
  const alsVorlage = async () => {
    const name = window.prompt('Name der neuen Vorlage (Split und Mahlzeiten gehen mit, die kcal flext auf den nächsten Kunden):', plan.name || 'Neue Vorlage');
    if (!name || name.trim().length < 2) return;
    try {
      const { data } = await ernaehrungAPI.vorlageAusPlan({ lead_id: leadId, plan_id: plan.id, name: name.trim() });
      setVorlagen((v) => [data, ...v.filter((x) => x.id !== data.id)]);
      toast.success(`Vorlage „${data.name}“ angelegt`, { action: { label: 'Öffnen', onClick: () => navigate(`/ernaehrung/vorlagen/${data.id}`) } });
    } catch (e) { toast.error(fehlertext(e)); }
  };
  const pdf = async () => {
    setLaeuft('pdf');
    try {
      const { data } = await ernaehrungAPI.planPdf(leadId, { plan_id: plan.id, von: plan.start, bis: plan.ende, art: 'plan' });
      const url = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url; a.download = 'ernaehrungsplan.pdf'; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e) { toast.error('PDF konnte nicht erstellt werden'); } finally { setLaeuft(''); }
  };
  const zugewiesen = plan && ['zugewiesen', 'aktiv'].includes(plan.status);
  const darf = !!plan && plan.status !== 'abgeloest';
  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="text-base">Plan-Builder</CardTitle>
          <div className="flex flex-wrap gap-1.5">
            <Button size="sm" variant={setupOffen && !variantenNeu ? 'secondary' : 'outline'} aria-expanded={setupOffen && !variantenNeu} onClick={() => { setSetupOffen(!(setupOffen && !variantenNeu)); setVariantenNeu(false); }}><Sparkles className="w-4 h-4 mr-1" />Neuer Plan</Button>
            <Button size="sm" variant={setupOffen && variantenNeu ? 'secondary' : 'outline'} aria-expanded={setupOffen && variantenNeu} onClick={() => { setSetupOffen(!(setupOffen && variantenNeu)); setVariantenNeu(true); }}><Sparkles className="w-4 h-4 mr-1" />Plan mit Varianten</Button>
          </div>
        </div>
        {liste === null ? <Skeleton className="h-8 w-full" /> : liste.plaene.length ? (
          <div role="tablist" aria-label="Pläne des Mitglieds" className="flex gap-1.5 overflow-x-auto py-1">
            {liste.plaene.map((p) => (
              <button key={p.id} type="button" role="tab" aria-selected={gewaehlt === p.id} onClick={() => setGewaehlt(p.id)}
                className={`shrink-0 rounded-md border px-2.5 py-1.5 text-left text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${gewaehlt === p.id ? 'border-primary bg-primary/5' : 'hover:bg-accent'}`}>
                <span className="flex items-center gap-1.5"><span className="font-medium">{p.name || `${p.tage} Tage`}{p.varianten ? ` · ${p.varianten} Varianten` : ''}</span><Badge variant="outline" className={`font-normal ${STATUS[p.status]?.cls || ''}`}>{STATUS[p.status]?.label || p.status}</Badge></span>
                <span className="block text-[11px] text-muted-foreground tabular-nums">{datum(p.start)} – {datum(p.ende)}{p.kcal ? ` · ${kcalText(p.kcal)} kcal` : ''}{p.split_name ? ` · ${p.split_name}` : ''}{p.laeuft ? ' · läuft' : ''}</span>
              </button>
            ))}
          </div>
        ) : <p className="text-sm text-muted-foreground">Noch kein Plan — links das Setup wählen und „Plan generieren“.</p>}
      </CardHeader>
      <CardContent>
        <div className={`grid gap-4 items-start ${setupOffen ? 'lg:grid-cols-[19rem_minmax(0,1fr)]' : 'grid-cols-1'}`}>
          {setupOffen && (
            <div className="rounded-lg border p-3">
              {variantenNeu ? (
                <VariantenNeu leadId={leadId} onAbbrechen={() => setVariantenNeu(false)}
                  onErzeugt={(data) => { setPlan(data); setGewaehlt(data.id); ladenListe(); setSetupOffen(false); if (data.status === 'zugewiesen') onPlanGeaendert?.(); }} />
              ) : (
              <Setup leadId={leadId} profil={profilMitNamen} liste={liste} vorlagen={vorlagen}
                onSplitsGeaendert={(splits) => setListe((l) => (l ? { ...l, splits } : l))}
                onErzeugt={(data) => { setPlan(data); setGewaehlt(data.id); ladenListe(); if (data.status === 'zugewiesen') onPlanGeaendert?.(); }} />
              )}
            </div>
          )}
          <div className="min-w-0 space-y-3">
            {!plan && gewaehlt && <Skeleton className="h-40 w-full" />}
            {!plan && !gewaehlt && <p className="text-sm text-muted-foreground">Einen Plan oben auswählen oder einen neuen generieren. Der Generator schließt Allergene und Abneigungen zuerst aus, wählt je Mahlzeit passende Rezepte und stimmt die Mengen je Zutat auf ± 5 % ab — gesperrte Zutaten bleiben fix.</p>}
            {plan && (
              <>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-semibold flex items-center gap-2 flex-wrap">{plan.name || 'Plan'} <Badge variant="outline" className={`font-normal ${STATUS[plan.status]?.cls || ''}`}>{STATUS[plan.status]?.label || plan.status}</Badge></p>
                    <p className="text-xs text-muted-foreground tabular-nums">{datum(plan.start, { day: '2-digit', month: '2-digit', year: 'numeric' })} – {datum(plan.ende, { day: '2-digit', month: '2-digit', year: 'numeric' })} · {kcalText(plan.ziele?.kcal)} kcal{plan.setup?.split_name ? ` · ${plan.setup.split_name}` : plan.setup?.split ? ` · P ${plan.setup.split.eiweiss_pct} / K ${plan.setup.split.kh_pct} / F ${plan.setup.split.fett_pct} %` : ''}{plan.setup?.kcal_modus === 'empfehlung' ? ' · kcal je Tag aus den Zielen' : ''}{plan.vorlage ? ` · nach „${plan.vorlage.name}“` : ''} · von {plan.erstellt_von || '–'}</p>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    <Button size="sm" variant="ghost" onClick={alsVorlage}><BookmarkPlus className="w-4 h-4 mr-1" />Als Vorlage</Button>
                    <Button size="sm" variant="ghost" disabled={laeuft === 'pdf'} onClick={pdf}><FileDown className="w-4 h-4 mr-1" />PDF</Button>
                    {darf && (zugewiesen
                      ? <Button size="sm" variant="outline" disabled={!!laeuft} onClick={() => status('entwurf')}><Undo2 className="w-4 h-4 mr-1" />Zuweisung aufheben</Button>
                      : <Button size="sm" disabled={!!laeuft} onClick={() => status('zugewiesen')}><Send className="w-4 h-4 mr-1" />Zuweisen</Button>)}
                    {plan.status === 'abgeloest' && <Button size="sm" variant="outline" disabled={!!laeuft} onClick={() => status('entwurf')}><Undo2 className="w-4 h-4 mr-1" />Als Entwurf weiterführen</Button>}
                    <Button size="sm" variant="ghost" className="text-destructive" disabled={!!laeuft} onClick={loeschen}><Trash2 className="w-4 h-4 mr-1" />Löschen</Button>
                  </div>
                </div>
                {plan.varianten && (
                  <VariantenEditor leadId={leadId} plan={plan} onPlan={(p) => { setPlan(p); ladenListe(); if (zugewiesen) onPlanGeaendert?.(); }} />
                )}
                {plan.varianten && <h4 className="text-sm font-semibold pt-2">Vorschau je Tag — so sieht es das Mitglied</h4>}
                {plan.ersetzt?.length > 0 && <p className="text-xs text-amber-700">{plan.ersetzt.length} Mahlzeit{plan.ersetzt.length === 1 ? '' : 'en'} aus der Vorlage ersetzt — Details in der Plan-Karte.</p>}
                <div role="tablist" aria-label="Tage" className="flex gap-1.5 overflow-x-auto py-1">
                  {plan.tage.map((t) => (
                    <button key={t.datum} type="button" role="tab" aria-selected={t.datum === aktiv} onClick={() => setAktiv(t.datum)}
                      className={`shrink-0 rounded-full border px-3 py-1 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${t.datum === aktiv ? 'bg-primary text-primary-foreground border-primary' : 'hover:bg-accent'} ${t.stand && !t.stand.treffer && t.datum !== aktiv ? 'border-amber-300' : ''}`}>
                      {datum(t.datum, { weekday: 'short', day: '2-digit', month: '2-digit' })}{t.datum === heute ? ' · heute' : ''}{t.variante ? ` · ${t.variante.titel}` : ''}
                    </button>
                  ))}
                </div>
                {tag && (
                  <>
                    <div className="rounded-lg border p-3 space-y-2">
                      <div className="flex flex-wrap items-baseline justify-between gap-2">
                        <p className="font-medium">Tag gesamt <span className="tabular-nums">{kcalText(tag.summe.kcal)}</span> <span className="text-muted-foreground font-normal">/ {kcalText(tag.stand?.ziel?.kcal)} kcal</span>{tag.ziele?.modus && tag.ziele.modus !== 'standard' ? <Badge variant="outline" className="ml-2 font-normal">{tag.ziele.modus}</Badge> : null}</p>
                        {tag.stand?.treffer ? <span className="text-xs text-emerald-700 inline-flex items-center gap-1"><Check className="w-3.5 h-3.5" aria-hidden="true" />innerhalb ± 5 %</span> : <span className="text-xs text-amber-700 tabular-nums">kcal {vorzeichen(tag.stand?.abweichung?.kcal)}</span>}
                      </div>
                      <MakroBalken stand={tag.stand} />
                      {tag.hinweis && <p className="text-xs text-muted-foreground">{tag.hinweis}</p>}
                    </div>
                    <div className="grid md:grid-cols-2 gap-3">
                      {tag.mahlzeiten.map((m) => (
                        <EditorMahlzeit key={m.slot} plan={plan} tag={tag} m={m} lebensmittel={lebensmittel} darf={darf && !plan.varianten} onZutat={zutat} onVerschieben={verschieben}
                          onTauschen={() => setTausch({ ...m, datum: tag.datum })} />
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground">Abweichung und Balken rechnet der Server (Ziel je Mahlzeit = Anteil × Tagesziel, ± 5 %). Mengen ändern, Rezept tauschen und Verschieben gehen gegen die Version des Plans — ändert jemand gleichzeitig, lädt der Plan neu.</p>
                  </>
                )}
                <TauschDialog leadId={leadId} plan={plan} ziel={tausch} onClose={() => setTausch(null)} onGetauscht={() => { ladenPlan(plan.id); if (zugewiesen) onPlanGeaendert?.(); }} />
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
